import { truncateToSalesforceCancellationReason } from './cancellationReasonLimits';
import {
  CANCELLATION_REASON_OTHER,
  cancellationReasons,
  type CancellationReason,
} from './cancellationReasons';

export const SCROLL_RETRY_DELAYS_MS = [0, 150, 400] as const;

type IndexFn = (index: number, length: number) => number;

/** WAI-ARIA radiogroup navigation for the vertical cancellation reason list. */
export const CANCELLATION_REASON_ARROW_NAVIGATION: Readonly<
  Record<string, IndexFn>
> = {
  ArrowDown: (index, length) => (index + 1) % length,
  ArrowRight: (index, length) => (index + 1) % length,
  ArrowUp: (index, length) => (index - 1 + length) % length,
  ArrowLeft: (index, length) => (index - 1 + length) % length,
  Home: () => 0,
  End: (_, length) => length - 1,
};

export const getCancellationReasonIndex = (
  selectedReason: CancellationReason | null,
  focusedReasonIndex: number,
): number => {
  if (!selectedReason) {
    return focusedReasonIndex;
  }

  const selectedIndex = cancellationReasons.indexOf(selectedReason);
  return selectedIndex >= 0 ? selectedIndex : focusedReasonIndex;
};

export const getNextCancellationReasonIndex = (
  key: string,
  selectedReason: CancellationReason | null,
  focusedReasonIndex: number,
  length: number,
): number | null => {
  const move = CANCELLATION_REASON_ARROW_NAVIGATION[key];
  if (!move) {
    return null;
  }

  const currentIndex = getCancellationReasonIndex(
    selectedReason,
    focusedReasonIndex,
  );

  return move(currentIndex, length);
};

export const getCancellationReasonRadioTabIndex = (
  index: number,
  selectedReason: CancellationReason | null,
  focusedReasonIndex: number,
): 0 | -1 => {
  if (selectedReason) {
    return cancellationReasons[index] === selectedReason ? 0 : -1;
  }

  return index === focusedReasonIndex ? 0 : -1;
};

export const getFocusTargetAfterRadiogroupTab = (
  selectedReason: CancellationReason | null,
): 'otherComment' | 'cancelOrKeep' =>
  selectedReason === CANCELLATION_REASON_OTHER
    ? 'otherComment'
    : 'cancelOrKeep';

export const getRadioLabelFontWeight = (isSelected: boolean): 400 | 500 =>
  isSelected ? 500 : 400;

export const buildCancellationReasonForApi = (
  selectedReason: CancellationReason,
  otherComment: string,
): string => {
  const trimmedComment = otherComment.trim();

  if (selectedReason === CANCELLATION_REASON_OTHER && trimmedComment) {
    return truncateToSalesforceCancellationReason(`Other: ${trimmedComment}`);
  }

  return truncateToSalesforceCancellationReason(selectedReason);
};

export const buildCancelAppointmentPayload = (
  selectedReason: CancellationReason | null,
  otherComment: string,
): string | null => {
  if (!selectedReason) {
    return null;
  }

  return buildCancellationReasonForApi(selectedReason, otherComment);
};

export const getModalOverflowScrollOffset = (
  containerRect: DOMRectReadOnly,
  elementRect: DOMRectReadOnly,
  padding = 16,
): number => {
  const overflowBottom = elementRect.bottom - containerRect.bottom + padding;
  return overflowBottom > 0 ? overflowBottom : 0;
};

export const scrollIntoModalView = (element: HTMLElement): void => {
  const scrollContainer = element.closest('.MuiDialogContent-root');

  if (!(scrollContainer instanceof HTMLElement)) {
    element.scrollIntoView({ behavior: 'smooth', block: 'end' });
    return;
  }

  const scrollOffset = getModalOverflowScrollOffset(
    scrollContainer.getBoundingClientRect(),
    element.getBoundingClientRect(),
  );

  if (scrollOffset > 0) {
    scrollContainer.scrollBy({ top: scrollOffset, behavior: 'smooth' });
  }
};

export const scheduleDelayedCallbacks = (
  callback: () => void,
  delaysMs: readonly number[],
): (() => void) => {
  const timeoutIds = delaysMs.map((delay) =>
    window.setTimeout(callback, delay),
  );

  return () => {
    for (const timeoutId of timeoutIds) {
      window.clearTimeout(timeoutId);
    }
  };
};
